import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { airtableService } from '../services/airtable';
import { formatToLocalTime, getMinutesFromNow, getCurrentUTCTime } from '../utils/timeUtils';
import { CATEGORIES, SCORE_LEVELS, LEVEL_DESCRIPTIONS, getDescriptors } from '../data/rubricData';

const StudentEvaluation = ({ user, currentSession }) => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [formData, setFormData] = useState({
    professionalism: null,
    problemSolving: null,
    clinicalSkill: null,
    requeueStudent: false,
    notes: ''
  });
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStudent();
    markStudentInProgress();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const loadStudent = async () => {
    try {
      const queueRecords = await airtableService.getQueueForSession(currentSession.sessionId);
      const studentRecord = queueRecords.find(record => record.id === studentId);

      if (studentRecord) {
        setStudent(studentRecord);
      } else {
        setError('Student not found in queue');
      }
    } catch (error) {
      console.error('Error loading student:', error);
      setError('Failed to load student information');
    }
  };

  const markStudentInProgress = async () => {
    try {
      await airtableService.updateQueueStatus(studentId, 'in_progress', getCurrentUTCTime());
    } catch (error) {
      console.error('Error updating student status:', error);
    }
  };

  const handleScoreSelect = (categoryKey, score) => {
    setFormData({ ...formData, [categoryKey]: score });
    setError('');
  };

  const handleNotesChange = (e) => {
    setFormData({ ...formData, notes: e.target.value });
  };

  const calculateResponseTime = () => {
    if (!student) return 0;
    return getMinutesFromNow(student.fields['Join Time']);
  };

  const getYearLevel = () => {
    return student?.fields['Year Level'] || 'D3';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const unanswered = CATEGORIES.filter(cat => formData[cat.key] === null);
    if (unanswered.length > 0) {
      setError(`Please score all categories: ${unanswered.map(c => c.shortLabel).join(', ')}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const responseTimeMinutes = calculateResponseTime();

      const evaluationData = {
        sessionId: currentSession.sessionId,
        studentName: student.fields['Student Name'],
        queueRecordId: studentId,
        yearLevel: getYearLevel(),
        professionalismScore: formData.professionalism,
        problemSolvingScore: formData.problemSolving,
        clinicalSkillScore: formData.clinicalSkill,
        requeueStudent: formData.requeueStudent,
        notes: formData.notes.trim(),
        evaluationTime: getCurrentUTCTime(),
        responseTimeMinutes
      };

      const evaluationRecord = await airtableService.createEvaluation(evaluationData);

      if (evaluationRecord) {
        if (formData.requeueStudent) {
          const queueRecords = await airtableService.getQueueForSession(currentSession.sessionId);
          const nextPosition = queueRecords.length + 1;

          const requeueData = {
            sessionId: currentSession.sessionId,
            studentName: student.fields['Student Name'],
            clinicCode: student.fields['Clinic Code'],
            joinTime: getCurrentUTCTime(),
            position: nextPosition,
            yearLevel: getYearLevel()
          };

          await airtableService.addToQueue(requeueData);
        }

        await airtableService.updateQueueStatus(studentId, 'completed');
        navigate('/dr-grant/dashboard');
      } else {
        setError('Failed to save evaluation. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting evaluation:', error);
      setError('Unable to submit evaluation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      await airtableService.updateQueueStatus(studentId, 'waiting');
      navigate('/dr-grant/dashboard');
    } catch (error) {
      console.error('Error updating student status:', error);
      navigate('/dr-grant/dashboard');
    }
  };

  if (!student) {
    return (
      <div className="container">
        <div className="card">
          <p>Loading student information...</p>
        </div>
      </div>
    );
  }

  const yearLevel = getYearLevel();

  const renderRubricCategory = (category) => {
    const selectedScore = formData[category.key];
    const descriptors = selectedScore ? getDescriptors(yearLevel, category.key, selectedScore) : [];
    const isExpanded = expandedCategory === category.key;

    return (
      <div key={category.key} style={{
        marginBottom: '20px',
        border: '2px solid var(--light-blue)',
        borderRadius: '10px',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '14px 16px',
          backgroundColor: 'var(--warm-cream)',
          borderBottom: '1px solid var(--light-blue)'
        }}>
          <h4 style={{ color: 'var(--text-dark)', margin: 0, fontSize: '15px' }}>
            {category.label}
          </h4>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '8px',
          padding: '12px'
        }}>
          {SCORE_LEVELS.map((level) => {
            const isSelected = selectedScore === level.score;
            return (
              <button
                key={level.score}
                type="button"
                onClick={() => handleScoreSelect(category.key, level.score)}
                style={{
                  padding: '10px 4px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  border: isSelected ? `3px solid ${level.color}` : '2px solid #e1e8ed',
                  backgroundColor: isSelected ? level.color : 'white',
                  color: isSelected ? 'white' : 'var(--text-dark)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minHeight: '56px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2px'
                }}
              >
                <span style={{ fontSize: '20px' }}>{level.shortLabel}</span>
                <span style={{ fontSize: '10px', fontWeight: 'normal', lineHeight: '1.2' }}>
                  {level.label}
                </span>
              </button>
            );
          })}
        </div>

        {selectedScore && (
          <div style={{
            padding: '0 12px 12px 12px'
          }}>
            <button
              type="button"
              onClick={() => setExpandedCategory(isExpanded ? null : category.key)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '13px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: SCORE_LEVELS.find(l => l.score === selectedScore)?.color + '18',
                color: SCORE_LEVELS.find(l => l.score === selectedScore)?.color,
                cursor: 'pointer',
                textAlign: 'left',
                fontWeight: '500'
              }}
            >
              {isExpanded ? '- Hide' : '+ View'} {yearLevel} descriptors for score {selectedScore}
            </button>

            {isExpanded && (
              <div style={{
                marginTop: '8px',
                padding: '12px',
                backgroundColor: SCORE_LEVELS.find(l => l.score === selectedScore)?.color + '0D',
                borderRadius: '6px',
                borderLeft: `3px solid ${SCORE_LEVELS.find(l => l.score === selectedScore)?.color}`
              }}>
                <p style={{
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: SCORE_LEVELS.find(l => l.score === selectedScore)?.color,
                  marginBottom: '6px'
                }}>
                  {LEVEL_DESCRIPTIONS[selectedScore]}
                </p>
                <ul style={{
                  margin: 0,
                  paddingLeft: '18px',
                  fontSize: '13px',
                  color: 'var(--text-dark)',
                  lineHeight: '1.5'
                }}>
                  {descriptors.map((desc, i) => (
                    <li key={i} style={{ marginBottom: '4px' }}>{desc}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container">
      <div className="header">
        <h1>Student Evaluation</h1>
        <p>Session: {currentSession?.clinicCode}</p>
      </div>

      <div className="card">
        <div style={{
          backgroundColor: 'var(--warm-cream)',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <h3 style={{ color: 'var(--text-dark)', marginBottom: '8px' }}>
            {student.fields['Student Name']}
            <span style={{
              marginLeft: '10px',
              padding: '3px 10px',
              fontSize: '14px',
              fontWeight: 'bold',
              borderRadius: '12px',
              backgroundColor: yearLevel === 'D4' ? '#1976D2' : '#7B1FA2',
              color: 'white'
            }}>
              {yearLevel}
            </span>
          </h3>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text-light)' }}>
              Joined Queue: {formatToLocalTime(student.fields['Join Time'])}
            </span>
            <span style={{ color: 'var(--text-light)' }}>
              Wait Time: {calculateResponseTime()} minutes
            </span>
            <span style={{ color: 'var(--text-light)' }}>
              Clinic: {student.fields['Clinic Code']}
            </span>
            {student.fields['Operatory Number'] && (
              <span style={{ color: 'var(--text-light)' }}>
                Operatory: #{student.fields['Operatory Number']}
              </span>
            )}
          </div>
          {student.fields['Queue Reason'] && (
            <div style={{ marginTop: '8px', color: 'var(--text-dark)', fontStyle: 'italic' }}>
              Reason: {student.fields['Queue Reason']}
            </div>
          )}
        </div>

        {error && (
          <div style={{
            color: 'var(--coral)',
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: '#ffe6e6',
            borderRadius: '6px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '8px' }}>
            <h3 style={{ color: 'var(--text-dark)', marginBottom: '4px' }}>
              {yearLevel} Daily Clinical Assessment Rubric
            </h3>
            <p style={{ color: 'var(--text-light)', fontSize: '14px', marginBottom: '16px' }}>
              Select a score (4-1) for each category. Tap the descriptor link to view criteria.
            </p>
          </div>

          {CATEGORIES.map(renderRubricCategory)}

          <div className="input-group" style={{ marginTop: '20px' }}>
            <label style={{ fontWeight: 'bold', marginBottom: '12px', display: 'block', fontSize: '16px' }}>
              Requeue student?
            </label>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, requeueStudent: true })}
                style={{
                  flex: 1,
                  padding: '16px 24px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  border: formData.requeueStudent === true ? '3px solid var(--muted-sage)' : '2px solid var(--light-blue)',
                  backgroundColor: formData.requeueStudent === true ? 'var(--muted-sage)' : 'white',
                  color: formData.requeueStudent === true ? 'white' : 'var(--text-dark)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minHeight: '60px'
                }}
              >
                YES
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, requeueStudent: false })}
                style={{
                  flex: 1,
                  padding: '16px 24px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  border: formData.requeueStudent === false ? '3px solid var(--coral)' : '2px solid var(--light-blue)',
                  backgroundColor: formData.requeueStudent === false ? 'var(--coral)' : 'white',
                  color: formData.requeueStudent === false ? 'white' : 'var(--text-dark)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minHeight: '60px'
                }}
              >
                NO
              </button>
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleNotesChange}
              placeholder="Enter any additional notes about this session..."
              rows="4"
              disabled={loading}
            />
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'space-between',
            flexWrap: 'wrap'
          }}>
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-secondary"
              disabled={loading}
              style={{ flex: 1, minWidth: '120px' }}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{ flex: 2, minWidth: '120px' }}
            >
              {loading ? 'Saving...' : 'Complete Evaluation'}
            </button>
          </div>
        </form>

        <div style={{
          marginTop: '24px',
          padding: '12px',
          backgroundColor: 'var(--light-blue)',
          borderRadius: '6px',
          fontSize: '14px',
          color: 'var(--text-dark)'
        }}>
          <strong>Tip:</strong> If you requeue the student, they will be added to the end of the current queue.
          The evaluation will still be saved for your records.
        </div>
      </div>
    </div>
  );
};

export default StudentEvaluation;
